// Rise/Fall Strategy Scanner + Bot — mounts BELOW the Pro Trade chart.
// Paper-trades 16 strategies across every Pro Trade pair, ranks them, hides
// losers, surfaces the best strategy + its live signal, and (optionally) lets
// the bot auto-place the TOP-ranked strategy's signal on the current pair.
// It fetches its own candle data via the public socket — it never touches the chart.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { TrendingUp, TrendingDown, Minus, Loader2, RefreshCw, Bot as BotIcon, Trophy, Server } from "lucide-react";
import { toast } from "sonner";
import { publicSend } from "@/lib/derivPublic";
import { api } from "@/lib/api";
import PaperTradesPanel from "@/components/dashboard/PaperTradesPanel";
import { scanAll, liveSignalFor, STRATEGIES } from "@/lib/riseFallStrategies";

const HORIZON = 2;                 // bars held per paper trade (2 min — shoot-out winner)
const SCAN_MS = 120000;            // full multi-pair rescan cadence
const LIVE_MS = 20000;             // current-pair live-signal / bot poll cadence
const SRV_MS = 15000;              // 24/7 server-bot status poll cadence
const BATCH = 6;                   // pairs fetched per batch (be gentle on the socket)

const fmtSig = (s) => (s === "CALL" ? "RISE" : s === "PUT" ? "FALL" : "—");

export const RiseFallScanner = ({ pairs = [], currentAsset, tfSec = 60, granularity, placeTrade, canTrade, riskHalt, accountId, accessToken = "", onBotStatusChange, registerBotControls }) => {
  const gran = granularity || 60;
  const isDemo = /^VR/i.test(accountId || "");
  const connected = !!accountId;
  const [rows, setRows] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const [showLosers, setShowLosers] = useState(false);
  const [running, setRunning] = useState(false);
  const [mode, setMode] = useState("paper");        // "paper" | "live"
  const [liveSig, setLiveSig] = useState(null);
  const [paper, setPaper] = useState({ trades: 0, wins: 0, net: 0, open: 0 });
  const runRef = useRef(false);
  const modeRef = useRef("paper");
  const lastFireRef = useRef("");
  const lastLiveFireRef = useRef({});                 // pair -> ts of last LIVE fire (10-min cooldown)
  const paperRef = useRef([]);                        // [{key,pair,side,entryPrice,resolveTime,resolved,win}]
  const busyRef = useRef(false);

  // ---- 24/7 server-side bot ----
  const [srvBot, setSrvBot] = useState(null);         // status payload or null
  const [engineMeta, setEngineMeta] = useState(null); // public engine facts (strategy count)
  useEffect(() => {
    api.get("/risefall-bot/meta").then(({ data }) => setEngineMeta(data)).catch(() => {});
  }, []);
  const [srvLoading, setSrvLoading] = useState(false);
  const [srvStake, setSrvStake] = useState(1);
  const [srvTp, setSrvTp] = useState("");
  const [srvSl, setSrvSl] = useState("");
  const srvRunning = !!srvBot?.running;
  useEffect(() => { onBotStatusChange?.(srvRunning); }, [srvRunning, onBotStatusChange]);

  // ---- Per-strategy P&L (REAL settled trades, ladder-grouped) ----
  const [stratPnl, setStratPnl] = useState([]);
  const [stratScope, setStratScope] = useState("daily"); // "daily" | "all"
  useEffect(() => {
    if (!accountId) { setStratPnl([]); return; }
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/pro-trade/strategy-pnl/${encodeURIComponent(accountId)}?scope=${stratScope}`);
        if (alive) setStratPnl(data?.strategies || []);
      } catch { /* ignore transient */ }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [accountId, stratScope]);

  const pollSrv = useCallback(async () => {
    if (!accountId) return;
    try {
      const { data } = await api.get(`/risefall-bot/status/${encodeURIComponent(accountId)}`);
      setSrvBot(data);
    } catch { /* ignore transient */ }
  }, [accountId]);

  useEffect(() => {
    if (!accountId) { setSrvBot(null); return; }
    pollSrv();
    const id = setInterval(pollSrv, SRV_MS);
    return () => clearInterval(id);
  }, [accountId, pollSrv]);

  const toggleSrv = useCallback(async () => {
    if (!accountId || !accessToken) { toast.error("Connect a Deriv account to run the 24/7 bot."); return; }
    setSrvLoading(true);
    try {
      if (srvRunning) {
        await api.post(`/risefall-bot/stop/${encodeURIComponent(accountId)}`);
        toast.success("24/7 Rise/Fall bot stopped");
      } else {
        const demo = /^(VRTC|VRW|DOT)/i.test(accountId || "");
        await api.post(`/risefall-bot/start`, {
          account_id: accountId,
          access_token: accessToken,
          stake: Math.max(0.35, Number(srvStake) || 1),
          disclaimer_accepted: true,
          daily_tp: Number(srvTp) > 0 ? Number(srvTp) : null,
          daily_sl: Number(srvSl) > 0 ? Number(srvSl) : null,
        });
        toast.success(`24/7 bot started · ${demo ? "DEMO" : "REAL"} · $${Math.max(0.35, Number(srvStake) || 1)}/trade`, {
          description: "Trades the top-ranked strategy continuously — even with this app closed.",
        });
      }
      await pollSrv();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not toggle the 24/7 bot");
    } finally { setSrvLoading(false); }
  }, [accountId, accessToken, srvRunning, srvStake, pollSrv]);

  // Quick-start hook: let the parent (Pro Trade header row) start/stop the
  // 24/7 bot with one tap without expanding the panel.
  useEffect(() => { registerBotControls?.({ toggle: toggleSrv, loading: srvLoading }); },
    [registerBotControls, toggleSrv, srvLoading]);

  const fetchCandles = useCallback(async (id) => {
    try {
      const r = await publicSend({ ticks_history: id, count: 300, end: "latest", style: "candles", granularity: gran, adjust_start_time: 1 });
      return (r?.candles || []).map((o) => ({ time: +o.epoch, open: +o.open, high: +o.high, low: +o.low, close: +o.close }));
    } catch { return null; }
  }, [gran]);

  const runScan = useCallback(async () => {
    if (busyRef.current || pairs.length === 0) return;
    busyRef.current = true; setScanning(true);
    try {
      const byPair = {};
      for (let i = 0; i < pairs.length; i += BATCH) {
        const batch = pairs.slice(i, i + BATCH);
        const res = await Promise.all(batch.map((p) => fetchCandles(p.id)));
        batch.forEach((p, j) => { if (res[j] && res[j].length >= 220) byPair[p.id] = res[j]; });
      }
      const ranked = scanAll(byPair, HORIZON);
      setRows(ranked); setLastScan(Date.now());
    } finally { busyRef.current = false; setScanning(false); }
  }, [pairs, fetchCandles]);

  useEffect(() => { runScan(); const id = setInterval(runScan, SCAN_MS); return () => clearInterval(id); }, [runScan]);
  useEffect(() => { runRef.current = running; }, [running]);
  useEffect(() => { modeRef.current = mode; }, [mode]);

  // v9 floor: the bot NEVER trades a strategy scanning below 56% win rate.
  // Among qualifiers, ranked by the WILSON LOWER BOUND of the win-rate —
  // prefers PROVEN accuracy (62% on 200 trades) over lucky streaks (71% on 12).
  const MIN_BOT_WR = 56;
  // Bayesian shrink toward 50/50 by sample size — a 66% win-rate on 12 scans
  // is treated as ~56%, so small-sample luck can't qualify as edge.
  const shrunkWr = (wrPct, n) => 50 + ((wrPct || 50) - 50) * ((n || 0) / ((n || 0) + 20));
  const wilson = (wrPct, n) => {
    if (!n) return 0;
    const p = Math.min(1, Math.max(0, wrPct / 100)), z = 1.96;
    const denom = 1 + (z * z) / n;
    const centre = p + (z * z) / (2 * n);
    const margin = z * Math.sqrt((p * (1 - p) + (z * z) / (4 * n)) / n);
    return (centre - margin) / denom;
  };
  const top = rows
    .filter((r) => r.net > 0 && r.winRate >= MIN_BOT_WR && r.trades >= 8 && shrunkWr(r.winRate, r.trades) >= 55)
    .sort((a, b) => wilson(b.winRate, b.trades) - wilson(a.winRate, a.trades) || b.net - a.net)[0] || null;
  const bestBelowFloor = !top && rows.some((r) => r.net > 0);

  const recomputePaper = () => {
    const done = paperRef.current.filter((t) => t.resolved);
    const wins = done.filter((t) => t.win).length;
    setPaper({ trades: done.length, wins, net: Number(done.reduce((a, t) => a + (t.win ? 0.95 : -1), 0).toFixed(2)), open: paperRef.current.length - done.length });
  };

  // Live signal for the TOP strategy on the CURRENT pair + paper/live engine.
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      if (!top || !currentAsset) { setLiveSig(null); return; }
      const c = await fetchCandles(currentAsset.id);
      if (!alive || !c || c.length < 2) return;
      const nowT = c[c.length - 2].time;
      // resolve open paper trades on this pair whose horizon elapsed
      let changed = false;
      paperRef.current.forEach((t) => {
        if (t.resolved || t.pair !== currentAsset.id) return;
        const bar = c.find((k) => k.time >= t.resolveTime);
        if (bar) { t.resolved = true; t.win = t.side === "CALL" ? bar.close > t.entryPrice : bar.close < t.entryPrice; changed = true; }
      });
      const sig = liveSignalFor(top.id, c);
      setLiveSig(sig);
      if (sig && runRef.current) {
        const key = `${top.id}:${currentAsset.id}:${nowT}:${sig}`;
        if (key !== lastFireRef.current) {
          lastFireRef.current = key;
          const entryPrice = c[c.length - 2].close;
          paperRef.current.push({ key, pair: currentAsset.id, side: sig, entryPrice, resolveTime: nowT + HORIZON * gran, resolved: false });
          if (paperRef.current.length > 400) paperRef.current = paperRef.current.slice(-400);
          changed = true;
          if (modeRef.current === "live") {
            if (!connected) toast.error("Connect a Deriv account to trade live.");
            else if (riskHalt) toast.error("Daily loss cap reached — bot paused.");
            else { placeTrade?.(sig); toast.success(`🤖 ${fmtSig(sig)} placed · ${top.name}`, { description: `${currentAsset.label} · ${isDemo ? "DEMO" : "REAL"} account` }); }
          }
        }
      }
      if (changed) recomputePaper();
    };
    tick(); const id = setInterval(tick, LIVE_MS);
    return () => { alive = false; clearInterval(id); };
  }, [top, currentAsset, fetchCandles, connected, riskHalt, placeTrade, isDemo, gran]);

  const visible = showLosers ? rows : rows.filter((r) => r.net > 0);
  const hiddenCount = rows.length - rows.filter((r) => r.net > 0).length;
  const recDur = HORIZON * gran;
  const recLabel = recDur >= 60 ? `${Math.round(recDur / 60)}m` : `${recDur}s`;

  return (
    <Card_ data-testid="risefall-scanner">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Trophy className="h-4 w-4 text-amber-400" />
          <span className="text-[14px] font-bold text-white">Rise/Fall Strategy Bot</span>
          <span className="text-[10px] text-vts-silver/50 font-mono">{srvBot?.strategy_count || engineMeta?.strategy_count || STRATEGIES.length} strategies · {pairs.length} pairs · {recLabel} expiry</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button" data-testid="risefall-rescan-btn" onClick={runScan} disabled={scanning}
            className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg border border-vts-line text-vts-silver/70 hover:text-white disabled:opacity-50">
            {scanning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Rescan
          </button>
        </div>
      </div>

      {/* 24/7 server-side bot — trades even with the app closed */}
      <div data-testid="risefall-server-bot" className="mb-3 flex items-center gap-2 flex-wrap rounded-lg border border-vts-blue/25 bg-vts-blue/5 p-2">
        <Server className="h-3.5 w-3.5 text-vts-blue" />
        <span className="text-[11px] font-semibold text-white">24/7 Server Bot</span>
        <span className="text-[10px] text-vts-silver/50">trades the top strategy even with this app closed</span>
        {srvRunning && (
          <span data-testid="risefall-server-stats" className="text-[10px] font-mono text-vts-silver/70">
            · {srvBot?.stats?.settled || 0} settled · {srvBot?.stats?.win_rate || 0}% ·{" "}
            <span className={(srvBot?.stats?.net || 0) >= 0 ? "text-vts-profit" : "text-vts-loss"}>
              net {(srvBot?.stats?.net || 0) >= 0 ? "+" : ""}{srvBot?.stats?.net || 0}
            </span>
            {srvBot?.open_contract ? " · 1 open" : ""}
          </span>
        )}
        {srvRunning && (
          <span data-testid="risefall-server-growth" className="text-[10px] font-mono text-amber-400/90">
            · streak {srvBot?.win_streak || 0}W · staking ${Number(srvBot?.current_stake || srvBot?.stake || 0).toFixed(2)}
          </span>
        )}
        {srvRunning && (
          <div className="ml-auto">
            <button
              type="button" data-testid="risefall-server-toggle" onClick={toggleSrv}
              disabled={srvLoading}
              className="flex items-center gap-1 text-[11px] font-semibold px-3 py-1.5 rounded-lg border transition disabled:opacity-40 border-vts-loss bg-vts-loss/20 text-vts-loss">
              {srvLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Server className="h-3.5 w-3.5" />}
              Stop 24/7
            </button>
          </div>
        )}
        {!srvRunning && (
          <div className="w-full space-y-2 mt-1">
            <div className="flex items-center gap-2">
              <label className="flex-1">
                <span className="text-[10px] text-vts-silver/60 block mb-0.5">Stake per trade ($)</span>
                <input
                  type="number" min="0.35" step="0.5" value={srvStake}
                  data-testid="risefall-server-stake"
                  onChange={(e) => setSrvStake(e.target.value)}
                  className="w-full rounded-md border border-vts-line bg-vts-surface px-2 py-1.5 text-[12px] text-white font-mono outline-none focus:border-vts-blue/60" />
                <span className="text-[9px] text-vts-silver/40 font-mono">$0.35 – $1000</span>
              </label>
            </div>
            <div>
              <div className="flex items-center justify-between flex-wrap gap-1">
                <span className="text-[11px] font-semibold text-white">Daily auto-stop <span className="text-vts-silver/50 font-normal">(optional)</span></span>
                <span className="text-[9px] text-vts-silver/45 font-mono">caps bad days · locks good ones</span>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <label className="flex items-center gap-1.5 rounded-md border border-vts-profit/30 bg-vts-profit/5 px-2 py-1.5" title="Optional: stop for the day after reaching this profit. Leave blank = no limit, the bot keeps running">
                  <span className="text-[10px] text-vts-profit font-semibold whitespace-nowrap">🎯 Take-profit</span>
                  <span className="text-vts-silver/50 text-[11px]">$</span>
                  <input
                    type="number" min="0.5" step="1" value={srvTp} placeholder="off"
                    data-testid="risefall-server-tp"
                    onChange={(e) => setSrvTp(e.target.value)}
                    className="w-full bg-transparent text-[12px] text-white font-mono outline-none" />
                </label>
                <label className="flex items-center gap-1.5 rounded-md border border-vts-loss/30 bg-vts-loss/5 px-2 py-1.5" title="Optional: stop for the day after losing this much. Leave blank = no limit">
                  <span className="text-[10px] text-vts-loss font-semibold whitespace-nowrap">🛑 Stop-loss</span>
                  <span className="text-vts-silver/50 text-[11px]">$</span>
                  <input
                    type="number" min="0.5" step="1" value={srvSl} placeholder="off"
                    data-testid="risefall-server-sl"
                    onChange={(e) => setSrvSl(e.target.value)}
                    className="w-full bg-transparent text-[12px] text-white font-mono outline-none" />
                </label>
              </div>
              <p className="text-[9px] text-vts-silver/45 font-mono mt-1">
                Leave blank to disable — the bot runs non-stop. With a limit set, it halts for the rest of the (UTC) day once today&apos;s realised P/L hits it.
              </p>
            </div>
            <button
              type="button" data-testid="risefall-server-toggle" onClick={toggleSrv}
              disabled={srvLoading || !accountId || !accessToken}
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-amber-400 text-black font-extrabold text-[14px] py-3 shadow-lg shadow-amber-400/25 hover:brightness-110 active:scale-[0.98] transition disabled:opacity-40">
              {srvLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Server className="h-4 w-4" />}
              ▶ START 24/7 AUTO BOT
            </button>
          </div>
        )}
        {(!accountId || !accessToken) && (
          <span className="w-full text-[10px] text-amber-400/80">Connect a Deriv account (log in) to enable the 24/7 server bot.</span>
        )}
        {srvBot?.last_error && (
          <span data-testid="risefall-server-error" className="w-full text-[10px] text-vts-loss/80 font-mono">last error: {srvBot.last_error}</span>
        )}
        {srvRunning && srvBot?.last_skip && (
          <span data-testid="risefall-server-skip" className="w-full text-[10px] text-amber-400/70 font-mono">⏸ {srvBot.last_skip}</span>
        )}
        {srvRunning && srvBot?.trade_duration && (
          <span data-testid="risefall-auto-duration" className="text-[10px] font-mono text-vts-silver/70 rounded-full border border-vts-line bg-vts-surface px-2.5 py-0.5">
            ⏱ {srvBot.trade_duration}m expiry · auto-picked from live results
          </span>
        )}
        {srvRunning && srvBot?.volume_tier && (
          <span data-testid="risefall-volume-tier" className="w-full text-[10px] font-mono text-amber-300 rounded-lg border border-amber-400/40 bg-amber-400/10 px-2 py-1.5 block">
            ⚡ <b className="text-white">Volume mode</b> — no strategy is fully accredited right now, so the bot trades the top-ranked setups at <b>HALF stake</b>, protected by your daily TP/SL rails. High risk — sessions stop automatically at the daily limit.
          </span>
        )}
        {srvRunning && srvBot?.last_skip?.includes("none are accredited") && (() => {
          const rows = (srvBot?.server_ranking || []).filter((r) => r.status === "proving" && (r.paper_wr ?? 0) >= 56);
          const best = rows.sort((a, b) => (b.paper_n || 0) - (a.paper_n || 0))[0];
          const need = srvBot?.accreditation?.provisional_trades ?? 15;
          return (
            <span data-testid="risefall-warmup-progress" className="w-full text-[10px] font-mono text-vts-blue/90 rounded-lg border border-vts-blue/30 bg-vts-blue/10 px-2 py-1.5 block">
              🔥 <b className="text-white">Warming up</b> — strategies must prove themselves on live sims before your money fires.
              {best
                ? <> Closest to unlocking: <b className="text-white">{best.name}</b> {Math.min(best.paper_n, need)}/{need} sims @ {best.paper_wr}% — first trades usually start within the hour.</>
                : <> Building the live sim record now — first trades usually start within the hour.</>}
            </span>
          );
        })()}
        {srvRunning && (srvBot?.blacklist || []).length > 0 && (
          <span data-testid="risefall-server-blacklist" className="w-full text-[10px] text-vts-loss/80 font-mono">
            🚫 auto-benched (losing on YOUR account, last 7d): {srvBot.blacklist.join(" · ")}
          </span>
        )}
        {srvRunning && (srvBot?.qualifiers_top || []).length === 0 && (
          <span data-testid="risefall-server-ranking" className="w-full text-[10px] text-vts-blue/90 font-mono">
            server ranking: nothing scans ≥{MIN_BOT_WR}% right now — bot is sitting out (by design)
          </span>
        )}
        {srvRunning && (
          <span data-testid="risefall-server-secure" className="w-full text-[10px] font-mono text-amber-300/90">
            🔒 Auto-Secure: peels at {(srvBot?.secure?.steps || [60, 75]).map((s) => `+${s}%`).join(" → ")} of max payout ·{" "}
            {srvBot?.secure?.keep_free ?? 2} of {srvBot?.secure?.ladder_n ?? 4} contracts always ride to expiry
          </span>
        )}
        {srvRunning && (
          <span data-testid="risefall-server-accred" className="w-full text-[10px] font-mono text-vts-silver/55">
            🧪 Accreditation: full stake after {srvBot?.accreditation?.min_trades ?? 30} LIVE paper trades at ≥{srvBot?.accreditation?.min_wr ?? 58}% · half stake from {srvBot?.accreditation?.provisional_trades ?? 15} sims · rolling {Math.round((srvBot?.accreditation?.window_h ?? 168) / 24)}-day window
            {(srvBot?.bench_today || []).length > 0 && (
              <> · ⛔ benched (2 losses → 1h cooldown): {srvBot.bench_today.join(" · ")}</>
            )}
          </span>
        )}
        {srvRunning && srvBot?.daily && (
          <span data-testid="risefall-server-daily" className={`w-full text-[10px] font-mono ${srvBot.daily.resting ? (srvBot.daily.net >= 0 ? "text-vts-profit" : "text-vts-loss") : "text-vts-silver/55"}`}>
            {srvBot.daily.resting
              ? srvBot.daily.resting
              : <>day net {srvBot.daily.net >= 0 ? "+" : "−"}${Math.abs(srvBot.daily.net || 0).toFixed(2)}{srvBot.daily.tp || srvBot.daily.sl
                  ? <> · stops at {srvBot.daily.tp ? `+$${Number(srvBot.daily.tp).toFixed(2)} TP` : "no TP"} / {srvBot.daily.sl ? `−$${Number(srvBot.daily.sl).toFixed(2)} SL` : "no SL"} (resets 00:00 UTC)</>
                  : <> · no daily limits — runs non-stop</>}</>}
          </span>
        )}
        <span data-testid="risefall-server-growth-note" className="w-full text-[10px] text-vts-silver/45">
          Growth engine: {srvBot?.strategy_count || engineMeta?.strategy_count || "100+"} strategies (trend · mean-reversion · volatility · price action · SMC) · every strategy paper-trades LIVE in the backend 24/7 — full stake after 30 live sims ≥58% (or 100+ sims ≥56%), HALF stake from 15 sims ≥56%, rolling 7-day window · trades its top-5 accredited strategies on their #1 pair (full-stake rows take slots first) — if nothing is accredited, VOLUME MODE trades the top-2 ranked setups at HALF stake behind the daily rails · instant blacklist re-check before EVERY entry · 2 losing ladders on a strategy = benched 1h from its last loss · a pair that just lost is benched 10 min · MANDATORY daily TP/SL rails (blank = defaults: bank +10× stake, cap −15× stake; resets 00:00 UTC) · win-rates SHRUNK by sample size before any money decision · MA 25/100 (15s) priority hunt must also earn accreditation · M15 structure bias (swing highs/lows + trend on the bigger timeframe) must confirm every entry · max ONE entry per pair per 3 min · 2-min pause after any losing ladder · stake +25% per 2 winning ladders ONLY on proven ≥62% setups (max 2×, resets on any loss).
        </span>
      </div>

      {/* BOT'S LIVE RANKING — the server's OWN scan, the EXACT list it trades from */}
      {srvRunning && (
        <div data-testid="risefall-server-heatmap" className="mb-3 rounded-lg border border-vts-blue/40 bg-vts-blue/5 p-2.5">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span className="text-[11px] font-semibold text-white">🤖 Bot&apos;s live ranking</span>
            <span className="text-[10px] text-vts-blue/80 font-mono">the server&apos;s own scan — this IS what the bot trades from</span>
            {srvBot?.last_scan_at && (
              <span className="ml-auto text-[9px] text-vts-silver/45 font-mono">
                scanned {new Date(srvBot.last_scan_at).toLocaleTimeString([], { hour12: false })}
              </span>
            )}
          </div>
          {(srvBot?.server_ranking || []).length === 0 ? (
            <p className="text-[11px] text-vts-silver/50 font-mono" data-testid="risefall-server-heatmap-empty">
              Nothing scans ≥{MIN_BOT_WR}% on the server right now — the bot is sitting out (by design). Only the MA 25/100 priority hunt can still fire.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-[9px] uppercase tracking-wider text-vts-silver/45">
                    <th className="py-1 pr-2">#</th><th className="py-1 pr-2">Strategy</th>
                    <th className="py-1 pr-2 text-right">Win%</th><th className="py-1 pr-2 text-right">Proven%</th>
                    <th className="py-1 pr-2 text-right">Samples</th><th className="py-1 pr-2 text-right">Net</th>
                    <th className="py-1 pr-2">Pair</th><th className="py-1 pr-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {srvBot.server_ranking.map((r) => (
                    <tr key={r.rank} data-testid={`risefall-server-heatmap-row-${r.rank}`}
                      className={`text-[11px] font-mono border-t border-vts-line/40 ${r.tradable ? "" : "opacity-50"}`}>
                      <td className="py-1 pr-2 text-vts-silver/60">{r.rank}</td>
                      <td className="py-1 pr-2 text-white">{r.name}</td>
                      <td className="py-1 pr-2 text-right text-vts-profit">{Number(r.win_rate).toFixed(1)}%</td>
                      <td className="py-1 pr-2 text-right text-vts-silver/70" title="win-rate shrunk by sample size — what the bot's money decisions use">{Number(r.shrunk_wr).toFixed(1)}%</td>
                      <td className="py-1 pr-2 text-right text-vts-silver/60">{r.trades}</td>
                      <td className={`py-1 pr-2 text-right ${r.net >= 0 ? "text-vts-profit" : "text-vts-loss"}`}>{r.net >= 0 ? "+" : "−"}{Math.abs(r.net).toFixed(2)}</td>
                      <td className="py-1 pr-2 text-vts-silver/70">{r.pair || "—"}</td>
                      <td className="py-1 pr-2">
                        {r.status === "benched" ? (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-vts-loss/15 text-vts-loss border border-vts-loss/30 uppercase tracking-wider" title="benched: losing on YOUR real results — the bot refuses to trade it">⛔ benched</span>
                        ) : r.tradable ? (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-vts-profit/15 text-vts-profit border border-vts-profit/30 uppercase tracking-wider" title={`accredited on ${r.paper_n || 0} live paper trades · ${r.paper_wr || 0}% — can trade real money`}>✅ trades</span>
                        ) : r.status === "accredited" ? (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-vts-profit/10 text-vts-profit/70 border border-vts-profit/20 uppercase tracking-wider" title="accredited but outside the top-5 — next in line">✅ ready</span>
                        ) : r.status === "provisional" ? (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-yellow-400/10 text-yellow-300 border border-yellow-400/30 uppercase tracking-wider" title={`provisional: ${r.paper_n || 0} live paper sims at ${r.paper_wr || 0}% — may trade at HALF stake until ${srvBot?.accreditation?.min_trades ?? 30} sims`}>🟡 half stake</span>
                        ) : (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-400/10 text-amber-300/90 border border-amber-400/25 uppercase tracking-wider" title={`proving: ${r.paper_n || 0}/${srvBot?.accreditation?.min_trades ?? 20} live paper trades${r.paper_n ? ` at ${r.paper_wr}%` : ""} — no real money until accredited`}>🧪 {r.paper_n || 0}/{srvBot?.accreditation?.min_trades ?? 20}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[9px] text-vts-silver/45 mt-1.5 font-mono">
                Only ✅ TRADES rows can fire real money, on their #1 pair, when a fresh signal + M5 trend agree. 🧪 rows are proving themselves on live paper trades first; ⛔ rows are benched on your real results. The MA 25/100 (15s) priority hunt runs on its own fast track but must ALSO earn accreditation on live paper trades (and the {MIN_BOT_WR}% floor) before real money.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Proving Ground — the bot's LIVE simulated trades + accreditation progress */}
      <PaperTradesPanel />

      {/* Browser bot (this-tab signals) — Paper / Live + account badge + Run toggle */}
      <div className="mb-3 flex items-center gap-2 flex-wrap rounded-lg border border-vts-line bg-vts-surface/40 p-2">
        <span className="w-full text-[10px] text-vts-silver/50 -mb-1">Browser bot — trades this tab&apos;s scan while the page is open (separate from the 24/7 server bot above)</span>
        <div className="flex items-center gap-1" data-testid="risefall-mode">
          <button type="button" data-testid="risefall-mode-paper" onClick={() => setMode("paper")}
            className={`px-2.5 py-1 rounded text-[11px] font-semibold border transition ${mode === "paper" ? "border-vts-blue bg-vts-blue/20 text-white" : "border-vts-line text-vts-silver/60 hover:text-white"}`}>📝 Paper</button>
          <button type="button" data-testid="risefall-mode-live" onClick={() => setMode("live")}
            className={`px-2.5 py-1 rounded text-[11px] font-semibold border transition ${mode === "live" ? "border-vts-loss bg-vts-loss/20 text-white" : "border-vts-line text-vts-silver/60 hover:text-white"}`}>⚡ Live</button>
        </div>
        <span data-testid="risefall-account-badge" className={`text-[10px] font-mono px-2 py-0.5 rounded ${!connected ? "bg-vts-surface text-vts-silver/50" : isDemo ? "bg-vts-blue/15 text-vts-blue" : "bg-amber-500/15 text-amber-400"}`}>
          {!connected ? "not connected" : isDemo ? "DEMO account" : "REAL account"}
        </span>
        <button
          type="button" data-testid="risefall-run-toggle" onClick={() => setRunning((v) => !v)}
          className={`ml-auto flex items-center gap-1 text-[11px] font-semibold px-3 py-1.5 rounded-lg border transition ${running ? "border-vts-profit bg-vts-profit/20 text-vts-profit" : "border-vts-line text-vts-silver/70 hover:text-white"}`}>
          <BotIcon className="h-3.5 w-3.5" /> {running ? "Running" : "Start bot"}
        </button>
      </div>

      {/* Per-strategy P&L — REAL settled trades, ladders counted as ONE */}
      {connected && (
        <div data-testid="risefall-strategy-pnl" className="mb-3 rounded-lg border border-vts-line/60 bg-vts-surface/30 p-2.5">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span className="text-[11px] font-semibold text-white">Strategy P&L</span>
            <span className="text-[10px] text-vts-silver/45">your real settled trades · ladders = 1 trade</span>
            <div className="ml-auto flex items-center gap-1">
              <button type="button" data-testid="risefall-strat-pnl-daily" onClick={() => setStratScope("daily")}
                className={`px-2 py-0.5 rounded text-[10px] font-mono border transition ${stratScope === "daily" ? "border-vts-blue bg-vts-blue/20 text-white" : "border-vts-line text-vts-silver/60 hover:text-white"}`}>Today</button>
              <button type="button" data-testid="risefall-strat-pnl-all" onClick={() => setStratScope("all")}
                className={`px-2 py-0.5 rounded text-[10px] font-mono border transition ${stratScope === "all" ? "border-vts-blue bg-vts-blue/20 text-white" : "border-vts-line text-vts-silver/60 hover:text-white"}`}>All-time</button>
            </div>
          </div>
          {stratPnl.length === 0 ? (
            <p className="text-[11px] text-vts-silver/45" data-testid="risefall-strat-pnl-empty">No settled trades {stratScope === "daily" ? "today" : "yet"}.</p>
          ) : (
            <div className="space-y-0.5">
              {stratPnl.map((s) => (
                <div key={s.strategy} data-testid={`risefall-strat-pnl-row-${s.strategy.replace(/[^a-zA-Z0-9]/g, "_")}`}
                  className="flex items-center gap-2 text-[11px] font-mono border-t border-vts-line/40 first:border-t-0 py-1">
                  <span className="text-white truncate min-w-0 flex-1" title={s.strategy}>{s.strategy}</span>
                  <span className="shrink-0"><span className="text-vts-profit">{s.wins}W</span><span className="text-vts-silver/40">/</span><span className="text-vts-loss">{s.losses}L</span></span>
                  <span className="shrink-0 text-vts-silver/55 w-10 text-right">{s.win_rate}%</span>
                  <span className={`shrink-0 w-16 text-right font-semibold ${s.net >= 0 ? "text-vts-profit" : "text-vts-loss"}`}>{s.net >= 0 ? "+" : "−"}${Math.abs(s.net).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Live paper P&L of the bot's actual signals */}
      {running && (
        <div data-testid="risefall-paper-stats" className="mb-3 flex items-center gap-3 flex-wrap text-[11px] font-mono rounded-lg border border-vts-line/60 bg-vts-surface/30 px-3 py-2">
          <span className="text-vts-silver/55">{mode === "live" ? "⚡ Live + tracked" : "📝 Paper (no real trades)"}</span>
          <span className="text-vts-silver/70">{paper.trades} settled</span>
          <span className="text-vts-silver/70">{paper.trades ? ((paper.wins / paper.trades) * 100).toFixed(0) : 0}% win</span>
          <span className={paper.net >= 0 ? "text-vts-profit" : "text-vts-loss"}>net {paper.net >= 0 ? "+" : ""}{paper.net}</span>
          <span className="text-vts-silver/45">{paper.open} open</span>
        </div>
      )}

      {/* Best strategy + live signal banner */}
      {top ? (
        <div data-testid="risefall-top-banner" className="mb-3 rounded-lg border border-amber-400/30 bg-amber-400/5 p-3 flex items-center gap-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-amber-400/80 font-semibold">Best for Rise/Fall now</span>
          <span className="text-[14px] font-bold text-white">{top.name}</span>
          <span className="text-[11px] font-mono text-vts-silver/70">{top.winRate}% win · net {top.net > 0 ? "+" : ""}{top.net} · {top.trades} sim trades</span>
          <span className="text-[11px] font-mono text-vts-silver/60">best pair: {top.bestPair}</span>
          <span className={`ml-auto flex items-center gap-1 text-[13px] font-bold ${liveSig === "CALL" ? "text-vts-profit" : liveSig === "PUT" ? "text-vts-loss" : "text-vts-silver/50"}`}>
            {liveSig === "CALL" ? <TrendingUp className="h-4 w-4" /> : liveSig === "PUT" ? <TrendingDown className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
            {currentAsset?.label}: {fmtSig(liveSig)}
          </span>
        </div>
      ) : (
        <div className="mb-3 text-[12px] text-vts-silver/55" data-testid="risefall-floor-note">
          {scanning ? "Scanning every pair…"
            : bestBelowFloor ? `Strategies are profitable but below the ${MIN_BOT_WR}% accuracy floor — the bot won't trade until one clears ${MIN_BOT_WR}%.`
            : "No profitable strategy in the current sample — waiting for the next scan."}
        </div>
      )}

      {/* Ranked table — BROWSER preview scan (not what the bot trades from) */}
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <span className="text-[10px] uppercase tracking-wider text-vts-silver/50 font-semibold">Browser preview scan</span>
        <span className="text-[9px] text-vts-silver/40 font-mono">computed in YOUR browser on its own data — the bot trades from the &quot;Bot&apos;s live ranking&quot; table above, not this</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left" data-testid="risefall-heatmap">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-vts-silver/45">
              <th className="py-1.5 pr-2">#</th><th className="py-1.5 pr-2">Strategy</th>
              <th className="py-1.5 pr-2 text-right">Win%</th><th className="py-1.5 pr-2 text-right">Net</th>
              <th className="py-1.5 pr-2 text-right">Trades</th><th className="py-1.5 pr-2">Best pair</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r, idx) => {
              const heat = r.net > 0 ? Math.min(1, r.net / 8) : 0;
              return (
                <tr key={r.id} data-testid={`risefall-row-${r.id}`} className={`text-[12px] border-t border-vts-line/60 ${idx === 0 ? "bg-amber-400/5" : ""}`}>
                  <td className="py-1.5 pr-2 font-mono text-vts-silver/50">{idx + 1}</td>
                  <td className="py-1.5 pr-2 text-white font-medium">{r.name}</td>
                  <td className="py-1.5 pr-2 text-right font-mono text-vts-silver/80">{r.winRate}%</td>
                  <td className="py-1.5 pr-2 text-right font-mono font-semibold" style={{ color: r.net > 0 ? `rgba(46,204,113,${0.55 + 0.45 * heat})` : "#ef4444" }}>{r.net > 0 ? "+" : ""}{r.net}</td>
                  <td className="py-1.5 pr-2 text-right font-mono text-vts-silver/55">{r.trades}</td>
                  <td className="py-1.5 pr-2 font-mono text-vts-silver/60">{r.bestPair || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
        <button type="button" data-testid="risefall-toggle-losers" onClick={() => setShowLosers((v) => !v)} className="text-[10px] text-vts-silver/45 hover:text-vts-silver/80">
          {showLosers ? "Hide losing strategies" : `${hiddenCount} losing ${hiddenCount === 1 ? "strategy" : "strategies"} hidden · show all`}
        </button>
        <span className="text-[10px] text-vts-silver/40 font-mono">{lastScan ? `updated ${new Date(lastScan).toLocaleTimeString()}` : ""}</span>
      </div>
      <p className="mt-2 text-[10px] text-vts-silver/40 leading-relaxed">
        Ranked by simulated net over recent candles (+0.95/win, −1.00/loss). <b>Paper</b> mode tracks the
        bot's live signals without placing real trades so you see accurate results first; <b>Live</b>
        mode places them on your connected {isDemo ? "demo" : "real"} Deriv account. The in-browser bot
        runs while this tab is open; the <b>24/7 Server Bot</b> keeps trading the top strategy on our
        servers even with this app closed. Past results don't guarantee profit.
      </p>
    </Card_>
  );
};

// Local card wrapper to match the terminal's surface styling.
const Card_ = ({ children, ...p }) => (
  <div {...p} className="rounded-xl border border-vts-line bg-vts-panel/60 p-4">{children}</div>
);

export default RiseFallScanner;
